<!--
// app/components/layout/header/Navbar.vue
-->
<script setup lang="ts">
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useWebContext } from "~/app/composables/webContext";
import { useWebappStore } from "~/app/stores/webappStore";
import { $TWITCH_INTERVAL_REFRESH } from "~/src/twitch/domain/services/twitchService";
import ContextLeagueLogo from "~/app/components/context-shared/ContextLeagueLogo.vue";
import ContextOtherLeaguesLogo from "~/app/components/context-shared/ContextOtherLeaguesLogo.vue";
import NavCompetition from "~/app/components/layout/header/NavCompetition.vue";
import NavRegions from "~/app/components/layout/header/NavRegions.vue";
import LangSwitcher from "~/app/components/layout/header/LangSwitcher.vue";
import ButtonsNavbar from "~/app/components/layout/header/ButtonsNavbar.vue";
import IconBurgerShowDropbar from "~/app/components/layout/header/IconBurgerShowDropbar.vue";
import DropbarMenuMobile from "~/app/components/layout/header/DropbarMenuMobile.vue";
import headerOptionsUiService from "~/src/options/infrastructure/ui-services/headerOptionsUiService";

const competitionStore = useCompetitionStore();
const { webContext, isKings, isQueens } = useWebContext();
const { locale } = useI18n();
const route = useRoute();
const webappStore = useWebappStore();
const { getHeaderButtonsOptions } = headerOptionsUiService();

const showNavMobile = ref<boolean>(false);
const showNavRegionsMobile = ref<boolean>(false);
const intervalId = ref<NodeJS.Timeout>();
const scrolling = ref<boolean>(false);

webappStore.headerButtonsData = await getHeaderButtonsOptions(competitionStore.region?.id);

const showMobileMenu = () => {
  const theBody: HTMLBodyElement | null = document.querySelector("body");
  showNavMobile.value = !showNavMobile.value;
  if (webappStore.activeRegion !== webappStore.regionHovered) {
    webappStore.regionHovered = webappStore.activeRegion;
  }
  if (theBody) {
    showNavMobile.value
      ? theBody.classList.add("overflow-hidden")
      : theBody.classList.remove("overflow-hidden");
  }
};

const handleBodyClass = () => {
  const theBody: HTMLBodyElement | null = document.querySelector("body");
  if (window.innerWidth > 1199) {
    showNavMobile.value = false;
    showNavRegionsMobile.value = false;
    if (theBody) {
      theBody.classList.remove("overflow-hidden");
    }
  }
};

const handleScroll = () => {
  // 29px és l'alçada aproximada del toolbar
  scrolling.value = window.scrollY > 28 && window.innerWidth > 1199;
};

const clearLiveInterval = () => {
  if (intervalId.value) {
    clearInterval(intervalId.value);
    intervalId.value = undefined;
  }
};

onMounted(async () => {
  window.addEventListener("resize", handleBodyClass);
  window.addEventListener("scroll", handleScroll);
  await nextTick();

  // Establir l'estat inicial del scroll
  handleScroll();

  await webappStore.updateTwitchStatus(locale.value);
  intervalId.value = setInterval(async function () {
    await webappStore.updateTwitchStatus(locale.value);
  }, $TWITCH_INTERVAL_REFRESH);
});

onUnmounted(() => {
  clearLiveInterval();
  window.removeEventListener("resize", handleBodyClass);
  window.removeEventListener("scroll", handleScroll);
});
</script>

<template>
  <div class="nav-regions" @mouseover="webappStore.hoveringMegaMenu = true">
    <div class="w-[152px]"></div>

    <nav-regions></nav-regions>

    <div class="ml-auto pr-5 rtl:mr-auto rtl:ml-0 rtl:pl-5 rtl:pr-0">
      <lang-switcher button-id="dropdownLangDesktop" dropdown-toggle="dropdownDesktop"></lang-switcher>
    </div>

    <context-other-leagues-logo></context-other-leagues-logo>
  </div>

  <div
    class="nav-competition"
    @mouseover="webappStore.hoveringMegaMenu = true"
    @mouseleave="webappStore.hoveringMegaMenu = false"
  >
    <div class="logo-wrapper" :class="scrolling ? 'h-full pb-4 lg:h-[91px]' : 'h-full lg:h-[120px]'">
      <context-league-logo
        border-shape
        height="h-full"
        :width="scrolling ? 'w-[38px] lg:w-[74px]' : 'w-[32px] lg:w-[88px]'"
      />
    </div>

    <nav-competition></nav-competition>

    <icon-burger-show-dropbar :show-nav-mobile="showNavMobile" @show-nav-mobile="showMobileMenu" />
  </div>

  <div class="w-full flex px-4 py-2 items-center justify-start gap-x-2 bg-black lg:hidden">
    <buttons-navbar></buttons-navbar>
  </div>

  <dropbar-menu-mobile v-model:show-nav-mobile="showNavMobile"></dropbar-menu-mobile>
</template>

<style scoped>
.nav-regions {
  @apply w-full pr-6 rtl:pl-6 rtl:pr-0 py-2 max-h-14 justify-start items-center bg-white hidden lg:flex;
}

.nav-competition {
  @apply w-full bg-white lg:bg-black relative z-20 lg:z-auto lg:static border-b-primary border-b px-4 py-2;
  @apply flex justify-start items-center h-[72px] lg:max-h-16 lg:pl-0 lg:py-3.5 lg:pr-6 lg:border-b-0;
  @apply rtl:lg:pl-6 rtl:lg:pr-0;
}

.logo-wrapper {
  @apply w-auto py-0 flex flex-col items-center justify-center lg:self-end lg:min-w-[152px];
  @apply lg:pt-2 lg:pb-3.5 transition-[height] ease-in-out duration-200;
}
</style>
